/**
 * GoogleIntegrations Settings Page
 *
 * Manage Google account connections for GSC, GA4, Ads, and Merchant Center.
 *
 * PRD: docs/delivery/stories/google-oauth-foundation/prd.md
 * GitHub Issue: #378
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Link2,
  Link2Off,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Clock,
  Search,
  BarChart3,
  Megaphone,
  ShoppingCart,
  ExternalLink,
  Upload,
  Shield,
  History,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import {
  googleIntegrationsService,
  type GoogleIntegrationStatus,
  type GoogleService,
  type GSCProperty,
  type GA4Property,
  type AdsAccount,
  type AuditLogEntry,
} from '@/services/googleIntegrationsService';

// Service configuration
const SERVICES: {
  id: GoogleService;
  name: string;
  description: string;
  icon: React.ReactNode;
  scope: string;
}[] = [
  {
    id: 'gsc',
    name: 'Search Console',
    description: 'Track search visibility, queries, and positions',
    icon: <Search className="h-5 w-5" />,
    scope: 'webmasters.readonly',
  },
  {
    id: 'ga4',
    name: 'Analytics 4',
    description: 'Track sessions, engagement, and conversions',
    icon: <BarChart3 className="h-5 w-5" />,
    scope: 'analytics.readonly',
  },
  {
    id: 'ads',
    name: 'Google Ads',
    description: 'Search terms, Quality Score, and landing pages',
    icon: <Megaphone className="h-5 w-5" />,
    scope: 'adwords',
  },
  {
    id: 'merchant_center',
    name: 'Merchant Center',
    description: 'Product status, disapprovals, and Shopping performance',
    icon: <ShoppingCart className="h-5 w-5" />,
    scope: 'content',
  },
];

export default function GoogleIntegrations() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const siteId = selectedSite?.id || selectedSite?._id;

  // State
  const [status, setStatus] = useState<GoogleIntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [selectedServices, setSelectedServices] = useState<GoogleService[]>(['gsc']);
  const [activeTab, setActiveTab] = useState('status');

  // Property selection
  const [gscProperties, setGscProperties] = useState<GSCProperty[]>([]);
  const [ga4Properties, setGa4Properties] = useState<GA4Property[]>([]);
  const [adsAccounts, setAdsAccounts] = useState<AdsAccount[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  // Service account modal
  const [showServiceAccountModal, setShowServiceAccountModal] = useState(false);
  const [serviceAccountKey, setServiceAccountKey] = useState('');
  const [merchantCenterId, setMerchantCenterId] = useState('');
  const [uploadingServiceAccount, setUploadingServiceAccount] = useState(false);

  // Disconnect modal
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Audit log
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [loadingAuditLog, setLoadingAuditLog] = useState(false);

  // Load integration status
  const loadStatus = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const data = await googleIntegrationsService.getStatus(siteId);
      setStatus(data);
    } catch (error) {
      console.error('Failed to load integration status:', error);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Load properties when connected
  useEffect(() => {
    if (status?.connected && siteId) {
      loadProperties();
    }
  }, [status?.connected, siteId]);

  const loadProperties = async () => {
    if (!siteId) return;
    setLoadingProperties(true);
    try {
      if (status?.services.gsc.connected) {
        const gsc = await googleIntegrationsService.getGSCProperties(siteId);
        setGscProperties(gsc.properties);
      }
      if (status?.services.ga4.connected) {
        const ga4 = await googleIntegrationsService.getGA4Properties(siteId);
        setGa4Properties(ga4.properties);
      }
      if (status?.services.ads.connected) {
        const ads = await googleIntegrationsService.getAdsAccounts(siteId);
        setAdsAccounts(ads.accounts);
      }
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setLoadingProperties(false);
    }
  };

  // Load audit log
  const loadAuditLog = async () => {
    if (!siteId) return;
    setLoadingAuditLog(true);
    try {
      const data = await googleIntegrationsService.getAuditLog(siteId);
      setAuditLog(data.logs);
    } catch (error) {
      console.error('Failed to load audit log:', error);
    } finally {
      setLoadingAuditLog(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'audit' && siteId) {
      loadAuditLog();
    }
  }, [activeTab, siteId]);

  // Connect Google account
  const handleConnect = async () => {
    if (!siteId || selectedServices.length === 0) return;
    setConnecting(true);
    try {
      const { authUrl } = await googleIntegrationsService.getAuthUrl(siteId, selectedServices);
      // Open OAuth in new window
      window.open(authUrl, '_blank', 'width=600,height=700');
      toast({
        title: 'Authorization Started',
        description: 'Complete the authorization in the popup window.',
      });
    } catch (error: any) {
      toast({
        title: 'Connection Failed',
        description: error.message || 'Failed to start OAuth flow',
        variant: 'destructive',
      });
    } finally {
      setConnecting(false);
    }
  };

  // Disconnect Google account
  const handleDisconnect = async () => {
    if (!siteId) return;
    setDisconnecting(true);
    try {
      await googleIntegrationsService.disconnect(siteId);
      toast({
        title: 'Disconnected',
        description: 'Google account has been disconnected.',
      });
      await loadStatus();
      setShowDisconnectModal(false);
    } catch (error: any) {
      toast({
        title: 'Disconnect Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setDisconnecting(false);
    }
  };

  // Upload service account
  const handleUploadServiceAccount = async () => {
    if (!siteId || !serviceAccountKey || !merchantCenterId) return;
    setUploadingServiceAccount(true);
    try {
      await googleIntegrationsService.uploadServiceAccount(siteId, serviceAccountKey, merchantCenterId);
      toast({
        title: 'Service Account Connected',
        description: 'Merchant Center service account has been configured.',
      });
      await loadStatus();
      setShowServiceAccountModal(false);
      setServiceAccountKey('');
      setMerchantCenterId('');
    } catch (error: any) {
      toast({
        title: 'Upload Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setUploadingServiceAccount(false);
    }
  };

  // Configure property
  const handleConfigureProperty = async (service: 'gsc' | 'ga4' | 'ads', value: string) => {
    if (!siteId) return;
    try {
      if (service === 'gsc') {
        await googleIntegrationsService.configure(siteId, { gscPropertyUrl: value });
      } else if (service === 'ga4') {
        const property = ga4Properties.find(p => p.propertyId === value);
        if (property) {
          await googleIntegrationsService.configure(siteId, {
            ga4PropertyId: property.propertyId,
            ga4PropertyName: property.propertyName,
            ga4AccountId: property.accountId,
          });
        }
      } else if (service === 'ads') {
        const account = adsAccounts.find(a => a.customerId === value);
        if (account) {
          await googleIntegrationsService.configure(siteId, {
            adsCustomerId: account.customerId,
            adsAccountName: account.descriptiveName,
          });
        }
      }
      toast({
        title: 'Property Selected',
        description: `${service.toUpperCase()} property has been configured.`,
      });
      await loadStatus();
    } catch (error: any) {
      toast({
        title: 'Configuration Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  // Trigger sync (fire-and-forget with polling)
  const handleSync = async (service: string) => {
    if (!siteId) return;
    setSyncing(service);
    try {
      let result;
      switch (service) {
        case 'gsc':
          result = await googleIntegrationsService.syncGSC(siteId);
          break;
        case 'ga4':
          result = await googleIntegrationsService.syncGA4(siteId);
          break;
        case 'ads':
          result = await googleIntegrationsService.syncAds(siteId);
          break;
        case 'shopping':
          result = await googleIntegrationsService.syncShopping(siteId);
          break;
        case 'merchant_center':
          result = await googleIntegrationsService.syncMerchantCenter(siteId);
          break;
      }

      toast({
        title: 'Sync Started',
        description: result?.message || 'Sync is running in the background.',
      });

      // Poll for completion every 5 seconds
      const pollInterval = setInterval(async () => {
        try {
          const currentStatus = await googleIntegrationsService.getStatus(siteId);
          setStatus(currentStatus);
          const syncStatus = currentStatus?.sync?.lastStatus;
          if (syncStatus && syncStatus !== 'syncing') {
            clearInterval(pollInterval);
            setSyncing(null);
            if (syncStatus === 'success') {
              toast({
                title: 'Sync Complete',
                description: `Synced ${currentStatus?.sync?.rowsProcessed || ''} rows successfully.`,
              });
            } else {
              toast({
                title: 'Sync Failed',
                description: currentStatus?.sync?.lastError || 'Unknown error',
                variant: 'destructive',
              });
            }
          }
        } catch {
          clearInterval(pollInterval);
          setSyncing(null);
        }
      }, 5000);

      // Safety timeout: stop polling after 10 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setSyncing(null);
      }, 10 * 60 * 1000);

    } catch (error: any) {
      toast({
        title: 'Sync Failed',
        description: error.message,
        variant: 'destructive',
      });
      setSyncing(null);
    }
  };

  // Toggle service selection
  const toggleService = (service: GoogleService) => {
    setSelectedServices(prev =>
      prev.includes(service)
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site to manage Google integrations.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <img src="https://www.google.com/favicon.ico" alt="Google" className="h-6 w-6" />
            Google Integrations
          </h1>
          <p className="text-muted-foreground mt-1">
            Connect Google services for {selectedSite?.name || 'your site'}
          </p>
        </div>
        {status?.connected && (
          <div className="flex items-center gap-2">
            <Badge variant={status.status === 'active' ? 'default' : 'secondary'}>
              {status.status === 'active' ? (
                <><CheckCircle2 className="h-3 w-3 mr-1" /> Connected</>
              ) : (
                <><Clock className="h-3 w-3 mr-1" /> {status.status}</>
              )}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDisconnectModal(true)}
            >
              <Link2Off className="h-4 w-4 mr-2" />
              Disconnect
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="status">Connection Status</TabsTrigger>
          <TabsTrigger value="configure">Configure</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="status" className="mt-4 space-y-6">
          {/* Not Connected State */}
          {!status?.connected && (
            <Card>
              <CardHeader>
                <CardTitle>Connect Google Account</CardTitle>
                <CardDescription>
                  Select the Google services you want to connect to PageMind
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {SERVICES.map(service => (
                    <div
                      key={service.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedServices.includes(service.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => toggleService(service.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          selectedServices.includes(service.id)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}>
                          {service.icon}
                        </div>
                        <div>
                          <h3 className="font-medium">{service.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {service.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Selected: {selectedServices.length} service(s)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowServiceAccountModal(true)}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Service Account
                    </Button>
                    <Button
                      onClick={handleConnect}
                      disabled={connecting || selectedServices.length === 0}
                    >
                      {connecting ? (
                        <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Connecting...</>
                      ) : (
                        <><Link2 className="h-4 w-4 mr-2" /> Connect with Google</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Connected State */}
          {status?.connected && (
            <>
              {/* Account Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Connected Account
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Email</div>
                      <div className="font-medium">{status.email || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Auth Method</div>
                      <div className="font-medium capitalize">{status.authMethod}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Connected</div>
                      <div className="font-medium">
                        {status.connectedAt
                          ? new Date(status.connectedAt).toLocaleDateString()
                          : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Last Sync</div>
                      <div className="font-medium">
                        {status.sync.lastSyncAt
                          ? new Date(status.sync.lastSyncAt).toLocaleString()
                          : 'Never'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Services Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SERVICES.map(service => {
                  const serviceStatus = status.services[service.id];
                  const isConnected = serviceStatus?.connected;

                  return (
                    <Card key={service.id}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg flex items-center gap-2">
                            {service.icon}
                            {service.name}
                          </CardTitle>
                          <Badge variant={isConnected ? 'default' : 'secondary'}>
                            {isConnected ? 'Connected' : 'Not Connected'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {isConnected ? (
                          <div className="space-y-3">
                            {service.id === 'gsc' && serviceStatus.propertyUrl && (
                              <div className="text-sm">
                                <span className="text-muted-foreground">Property: </span>
                                <span className="font-medium">{serviceStatus.propertyUrl}</span>
                              </div>
                            )}
                            {service.id === 'ga4' && serviceStatus.propertyName && (
                              <div className="text-sm">
                                <span className="text-muted-foreground">Property: </span>
                                <span className="font-medium">{serviceStatus.propertyName}</span>
                              </div>
                            )}
                            {service.id === 'ads' && serviceStatus.customerId && (
                              <div className="text-sm">
                                <span className="text-muted-foreground">Customer ID: </span>
                                <span className="font-medium">{serviceStatus.customerId}</span>
                              </div>
                            )}
                            {service.id === 'merchant_center' && serviceStatus.merchantId && (
                              <div className="text-sm">
                                <span className="text-muted-foreground">Merchant ID: </span>
                                <span className="font-medium">{serviceStatus.merchantId}</span>
                              </div>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSync(service.id)}
                              disabled={syncing === service.id}
                            >
                              {syncing === service.id ? (
                                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Syncing...</>
                              ) : (
                                <><RefreshCw className="h-4 w-4 mr-2" /> Sync Now</>
                              )}
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {service.description}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Sync Status */}
              {status.sync.lastError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Last sync error: {status.sync.lastError}
                    {status.sync.consecutiveFailures && status.sync.consecutiveFailures > 1 && (
                      <span className="ml-2">
                        ({status.sync.consecutiveFailures} consecutive failures)
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="configure" className="mt-4 space-y-6">
          {!status?.connected ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Connect a Google account first to configure properties.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {/* GSC Property Selection */}
              {status.services.gsc.connected && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Search className="h-5 w-5" />
                      Search Console Property
                    </CardTitle>
                    <CardDescription>
                      Select which Search Console property to sync
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingProperties ? (
                      <Skeleton className="h-10 w-full" />
                    ) : (
                      <Select
                        value={status.services.gsc.propertyUrl || ''}
                        onValueChange={(value) => handleConfigureProperty('gsc', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a property" />
                        </SelectTrigger>
                        <SelectContent>
                          {gscProperties.map(prop => (
                            <SelectItem key={prop.siteUrl} value={prop.siteUrl}>
                              {prop.siteUrl} ({prop.permissionLevel})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* GA4 Property Selection */}
              {status.services.ga4.connected && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Analytics 4 Property
                    </CardTitle>
                    <CardDescription>
                      Select which GA4 property to sync
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingProperties ? (
                      <Skeleton className="h-10 w-full" />
                    ) : (
                      <Select
                        value={status.services.ga4.propertyId || ''}
                        onValueChange={(value) => handleConfigureProperty('ga4', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a property" />
                        </SelectTrigger>
                        <SelectContent>
                          {ga4Properties.map(prop => (
                            <SelectItem key={prop.propertyId} value={prop.propertyId}>
                              {prop.propertyName} ({prop.accountName})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Google Ads Account Selection */}
              {status.services.ads.connected && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Megaphone className="h-5 w-5" />
                      Google Ads Account
                    </CardTitle>
                    <CardDescription>
                      Select which Ads account to sync campaign data from
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingProperties ? (
                      <Skeleton className="h-10 w-full" />
                    ) : (
                      <Select
                        value={status.services.ads.customerId || ''}
                        onValueChange={(value) => handleConfigureProperty('ads', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select an account" />
                        </SelectTrigger>
                        <SelectContent>
                          {adsAccounts.map(account => (
                            <SelectItem key={account.customerId} value={account.customerId}>
                              {account.descriptiveName} ({account.customerId})
                              {account.isManager ? ' [Manager]' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Audit Log
              </CardTitle>
              <CardDescription>
                History of integration operations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingAuditLog ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : auditLog.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No audit log entries yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {auditLog.map(entry => (
                    <div
                      key={entry.id}
                      className="p-3 border rounded-lg flex items-center justify-between"
                    >
                      <div>
                        <div className="font-medium capitalize">{entry.action}</div>
                        <div className="text-sm text-muted-foreground">
                          {entry.actor_email || 'System'} - {entry.ip_address}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(entry.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Disconnect Confirmation Modal */}
      <Dialog open={showDisconnectModal} onOpenChange={setShowDisconnectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect Google Account?</DialogTitle>
            <DialogDescription>
              This will remove all Google integrations for this site.
              Synced data will be preserved but no new data will be collected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisconnectModal(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Account Modal */}
      <Dialog open={showServiceAccountModal} onOpenChange={setShowServiceAccountModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Service Account Key</DialogTitle>
            <DialogDescription>
              For Merchant Center integration, you can use a service account instead of OAuth.
              Create a service account in Google Cloud Console and download the JSON key.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="merchantCenterId">Merchant Center ID</Label>
              <Input
                id="merchantCenterId"
                value={merchantCenterId}
                onChange={(e) => setMerchantCenterId(e.target.value)}
                placeholder="123456789"
              />
            </div>
            <div>
              <Label htmlFor="serviceAccountKey">Service Account Key (JSON)</Label>
              <Textarea
                id="serviceAccountKey"
                value={serviceAccountKey}
                onChange={(e) => setServiceAccountKey(e.target.value)}
                placeholder='{"type": "service_account", "project_id": "...", ...}'
                rows={10}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceAccountModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadServiceAccount}
              disabled={uploadingServiceAccount || !serviceAccountKey || !merchantCenterId}
            >
              {uploadingServiceAccount ? 'Uploading...' : 'Upload & Connect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
